import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Car,
  Bike,
  Truck,
  Plus,
  Trash2,
  RefreshCw,
  Check,
  CarFront,
} from 'lucide-react';
import api from '../../utils/api';

const VEHICLE_TYPES = [
  { code: 'carros', label: 'Carros', Icon: Car },
  { code: 'motos', label: 'Motos', Icon: Bike },
  { code: 'caminhoes', label: 'Caminhões', Icon: Truck },
];

const fmtBRL = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
};

// Converte "R$ 50.000,00" → 50000
const parseFipeValor = (raw) => {
  if (!raw) return 0;
  return (
    parseFloat(
      String(raw)
        .replace(/[^\d,.-]/g, '')
        .replace(/\./g, '')
        .replace(',', '.')
    ) || 0
  );
};

const VeiculoFipeCard = ({ formData, setFormData }) => {
  const veiculos = formData.veiculos || [];
  const [open, setOpen] = useState(false);

  const [vehicleType, setVehicleType] = useState('carros');
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);

  const [brand, setBrand] = useState(null);
  const [model, setModel] = useState(null);
  const [year, setYear] = useState(null);
  const [result, setResult] = useState(null);

  const [loading, setLoading] = useState({
    brands: false,
    models: false,
    years: false,
    value: false,
  });

  const resetSelection = () => {
    setBrand(null);
    setModel(null);
    setYear(null);
    setResult(null);
    setModels([]);
    setYears([]);
  };

  // Brands when type changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading((s) => ({ ...s, brands: true }));
      setBrands([]);
      resetSelection();
      try {
        const { data } = await api.get(`/fipe/${vehicleType}/brands`);
        if (!cancelled) setBrands(data || []);
      } catch (e) {
        if (!cancelled) console.error('Erro ao carregar marcas FIPE', e);
      } finally {
        if (!cancelled) setLoading((s) => ({ ...s, brands: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType, open]);

  // Models when brand changes
  useEffect(() => {
    if (!brand) return;
    let cancelled = false;
    (async () => {
      setLoading((s) => ({ ...s, models: true }));
      setModels([]);
      setYears([]);
      setModel(null);
      setYear(null);
      setResult(null);
      try {
        const { data } = await api.get(
          `/fipe/${vehicleType}/brands/${brand.codigo}/models`
        );
        if (!cancelled) setModels(data?.modelos || []);
      } catch (e) {
        if (!cancelled) console.error('Erro ao carregar modelos FIPE', e);
      } finally {
        if (!cancelled) setLoading((s) => ({ ...s, models: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brand, vehicleType]);

  // Years when model changes
  useEffect(() => {
    if (!brand || !model) return;
    let cancelled = false;
    (async () => {
      setLoading((s) => ({ ...s, years: true }));
      setYears([]);
      setYear(null);
      setResult(null);
      try {
        const { data } = await api.get(
          `/fipe/${vehicleType}/brands/${brand.codigo}/models/${model.codigo}/years`
        );
        if (!cancelled) setYears(data || []);
      } catch (e) {
        if (!cancelled) console.error('Erro ao carregar anos FIPE', e);
      } finally {
        if (!cancelled) setLoading((s) => ({ ...s, years: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brand, model, vehicleType]);

  // Value when year changes
  useEffect(() => {
    if (!brand || !model || !year) return;
    let cancelled = false;
    (async () => {
      setLoading((s) => ({ ...s, value: true }));
      setResult(null);
      try {
        const { data } = await api.get(
          `/fipe/${vehicleType}/brands/${brand.codigo}/models/${model.codigo}/years/${year.codigo}`
        );
        if (!cancelled) setResult(data);
      } catch (e) {
        if (!cancelled) console.error('Erro ao buscar valor FIPE', e);
      } finally {
        if (!cancelled) setLoading((s) => ({ ...s, value: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brand, model, year, vehicleType]);

  const currentStep = useMemo(() => {
    if (result) return 5;
    if (year) return 4;
    if (model) return 3;
    if (brand) return 2;
    return 1;
  }, [brand, model, year, result]);

  const totalVeiculos = useMemo(
    () => veiculos.reduce((acc, v) => acc + (Number(v.valor) || 0), 0),
    [veiculos]
  );

  const adicionarVeiculo = () => {
    if (!result) return;
    const novo = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      tipo: vehicleType,
      marca: brand?.nome || result.Marca,
      modelo: result.Modelo,
      ano: result.AnoModelo,
      combustivel: result.Combustivel,
      codigo_fipe: result.CodigoFipe,
      mes_referencia: result.MesReferencia,
      valor: parseFipeValor(result.Valor),
      valor_formatado: result.Valor,
    };
    setFormData({ ...formData, veiculos: [...veiculos, novo] });
    resetSelection();
  };

  const removerVeiculo = (id) => {
    setFormData({
      ...formData,
      veiculos: veiculos.filter((v) => v.id !== id),
    });
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="card-veiculo-fipe">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg text-slate-800">
            <CarFront className="text-primary" size={20} />
            Veículos no Negócio
            {veiculos.length > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                {veiculos.length} • {fmtBRL(totalVeiculos)}
              </span>
            )}
          </CardTitle>
          <Button
            type="button"
            variant={open ? 'outline' : 'default'}
            onClick={() => setOpen((o) => !o)}
            data-testid="btn-inserir-veiculo"
            className="h-9"
          >
            <Plus size={16} className="mr-1" />
            {open ? 'Fechar' : 'Inserir Veículo no Negócio'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Lista de veículos já adicionados */}
        {veiculos.length > 0 && (
          <div className="space-y-2" data-testid="lista-veiculos">
            {veiculos.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
                data-testid={`veiculo-item-${v.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    {v.tipo === 'motos' ? (
                      <Bike size={18} />
                    ) : v.tipo === 'caminhoes' ? (
                      <Truck size={18} />
                    ) : (
                      <Car size={18} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">
                      {v.marca} • {v.modelo}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {v.ano} • {v.combustivel} • FIPE {v.codigo_fipe} •{' '}
                      {v.mes_referencia?.trim()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm font-semibold text-emerald-700 font-mono">
                    {v.valor_formatado || fmtBRL(v.valor)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removerVeiculo(v.id)}
                    data-testid={`remover-veiculo-${v.id}`}
                    className="text-slate-400 hover:text-red-600 transition-colors p-1"
                    title="Remover veículo"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Painel de inclusão (FIPE) */}
        {open && (
          <div
            className="rounded-xl border border-dashed border-emerald-200 bg-emerald-50/40 p-5 space-y-5"
            data-testid="painel-fipe"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                  Consulta FIPE
                </div>
                <div className="text-sm text-slate-600">
                  Etapa {currentStep} de 5
                </div>
              </div>
              {(brand || model || year || result) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={resetSelection}
                  data-testid="btn-fipe-reset"
                  className="h-8"
                >
                  <RefreshCw size={14} className="mr-1" />
                  Nova consulta
                </Button>
              )}
            </div>

            {/* Step 1 — Tipo */}
            <div className="space-y-2">
              <Label className="input-label">Tipo de veículo</Label>
              <div className="grid grid-cols-3 gap-2">
                {VEHICLE_TYPES.map((t) => {
                  const Icon = t.Icon;
                  const active = vehicleType === t.code;
                  return (
                    <button
                      key={t.code}
                      type="button"
                      data-testid={`fipe-tipo-${t.code}`}
                      onClick={() => setVehicleType(t.code)}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        active
                          ? 'border-emerald-600 bg-emerald-600 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300'
                      }`}
                    >
                      <Icon size={16} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2 — Marca */}
            <div className="space-y-2">
              <Label className="input-label">Marca</Label>
              {loading.brands ? (
                <Skeleton className="h-11 w-full rounded-md" />
              ) : (
                <Select
                  value={brand?.codigo || ''}
                  onValueChange={(val) => {
                    const found = brands.find((b) => b.codigo === val);
                    setBrand(found || null);
                  }}
                  disabled={brands.length === 0}
                >
                  <SelectTrigger
                    className="h-11 bg-white"
                    data-testid="fipe-select-marca"
                  >
                    <SelectValue placeholder="Selecione a marca" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {brands.map((b) => (
                      <SelectItem key={b.codigo} value={b.codigo}>
                        {b.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Step 3 — Modelo */}
            {brand && (
              <div className="space-y-2">
                <Label className="input-label">Modelo</Label>
                {loading.models ? (
                  <Skeleton className="h-11 w-full rounded-md" />
                ) : (
                  <Select
                    value={model?.codigo ? String(model.codigo) : ''}
                    onValueChange={(val) => {
                      const found = models.find(
                        (m) => String(m.codigo) === val
                      );
                      setModel(found || null);
                    }}
                    disabled={models.length === 0}
                  >
                    <SelectTrigger
                      className="h-11 bg-white"
                      data-testid="fipe-select-modelo"
                    >
                      <SelectValue placeholder="Selecione o modelo" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {models.map((m) => (
                        <SelectItem
                          key={m.codigo}
                          value={String(m.codigo)}
                        >
                          {m.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Step 4 — Ano */}
            {model && (
              <div className="space-y-2">
                <Label className="input-label">Ano e combustível</Label>
                {loading.years ? (
                  <Skeleton className="h-11 w-full rounded-md" />
                ) : (
                  <Select
                    value={year?.codigo || ''}
                    onValueChange={(val) => {
                      const found = years.find((y) => y.codigo === val);
                      setYear(found || null);
                    }}
                    disabled={years.length === 0}
                  >
                    <SelectTrigger
                      className="h-11 bg-white"
                      data-testid="fipe-select-ano"
                    >
                      <SelectValue placeholder="Selecione o ano" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {years.map((y) => (
                        <SelectItem key={y.codigo} value={y.codigo}>
                          {y.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Step 5 — Resultado */}
            {year && (
              <div className="space-y-3" data-testid="fipe-resultado">
                {loading.value ? (
                  <Skeleton className="h-28 w-full rounded-lg" />
                ) : result ? (
                  <div className="rounded-xl border border-emerald-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-700 mb-1 flex items-center gap-1">
                          <Check size={12} /> Valor FIPE
                        </div>
                        <div className="font-semibold text-slate-800 truncate">
                          {result.Marca} • {result.Modelo}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {result.AnoModelo} • {result.Combustivel} • Código{' '}
                          {result.CodigoFipe} • Ref{' '}
                          {result.MesReferencia?.trim()}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-2xl font-bold text-emerald-700 font-mono">
                          {result.Valor}
                        </div>
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={adicionarVeiculo}
                      data-testid="btn-adicionar-veiculo"
                      className="w-full mt-4 bg-emerald-700 hover:bg-emerald-800"
                    >
                      <Plus size={16} className="mr-1" />
                      Adicionar este veículo ao negócio
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">
                    Sem resultado para esta combinação.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!open && veiculos.length === 0 && (
          <div
            className="text-sm text-slate-500 italic"
            data-testid="empty-veiculos"
          >
            Nenhum veículo incluído. Clique em "Inserir Veículo no Negócio" para
            consultar a tabela FIPE e adicionar.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VeiculoFipeCard;
